<script setup lang="ts">
  export type HeroCarouselSlide = {
    image: string
    wordmark: string
    tagline: string
    align: 'center' | 'bottom-left'
    cta: { to: string; label: string }
  }

  const props = withDefaults(
    defineProps<{
      slides: HeroCarouselSlide[]
      /** Autoplay interval in ms */
      interval?: number
    }>(),
    {
      interval: 5000,
    },
  )

  /** Warm up images after first slide so switches don't flash empty. */
  function preloadSlideImages() {
    props.slides.slice(1).forEach((slide) => {
      const img = new Image()
      img.src = slide.image
    })
  }

  onMounted(() => {
    preloadSlideImages()
  })
</script>

<template>
  <n-carousel
    class="hero-carousel"
    draggable
    touchable
    autoplay
    :interval="interval"
    effect="slide"
    show-dots
    :show-arrow="false"
    dot-type="dot"
    dot-placement="bottom"
    trigger="click"
  >
    <div v-for="(slide, i) in slides" :key="`${slide.image}-${i}`" class="hero-slide">
      <img
        class="hero-slide-img"
        :src="slide.image"
        :alt="slide.wordmark"
        :fetchpriority="i === 0 ? 'high' : 'low'"
        :loading="i === 0 ? 'eager' : 'lazy'"
        decoding="async"
        draggable="false"
      />
      <div class="hero-overlay" :class="slide.align === 'center' ? 'is-center' : 'is-bottom-left'">
        <div class="hero-content">
          <div class="hero-wordmark">{{ slide.wordmark }}</div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p class="hero-tagline" v-html="slide.tagline" />
          <NuxtLink :to="slide.cta.to" class="hero-cta">{{ slide.cta.label }}</NuxtLink>
        </div>
      </div>
    </div>
  </n-carousel>
</template>

<style lang="scss" scoped>
  .hero-carousel {
    width: 100%;
    height: 100%;
  }

  .hero-carousel :deep(.n-carousel__slides),
  .hero-carousel :deep(.n-carousel__slide),
  .hero-carousel :deep(.n-carousel__slide > div) {
    height: 100%;
  }

  .hero-slide {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    opacity: 1;
    pointer-events: auto;
  }

  /* Keep indicator position & look aligned with legacy .hero-dots */
  .hero-carousel :deep(.n-carousel__dots) {
    position: absolute;
    bottom: 40px;
    right: 40px;
    left: auto;
    width: auto;
    transform: none;
    display: flex;
    gap: 8px;
    z-index: 10;
  }

  .hero-carousel :deep(.n-carousel__dot) {
    width: 10px !important;
    height: 10px !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 50% !important;
    background: rgba(255, 255, 255, 0.4) !important;
    opacity: 1 !important;
    transition: width 0.3s ease, background 0.3s ease, border-radius 0.3s ease;
  }

  .hero-carousel :deep(.n-carousel__dot--active) {
    width: 50px !important;
    border-radius: 20px !important;
    background: #fff !important;
  }
</style>
